/**
 * Thin fetch wrapper for the Hub SPA.
 *
 * - Always sends cookies (same-origin in prod; `credentials: 'include'` for dev
 *   when SPA runs on :5173 and hub on :5000 behind Vite proxy).
 * - Normalises errors into `ApiError` so views can show user-friendly text.
 * - CSRF is intentionally absent: `CsrfGuard` exists on the backend but is
 *   not wired to any controller yet (see services/hub/src/common/guards/csrf.guard.ts).
 *   When it lands, add `X-CSRF-Token` here in one spot.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    throw new ApiError(0, 'network_error', (err as Error).message || 'Network error', null);
  }

  // 204 — no body.
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Non-JSON body — keep as text.
      parsed = text;
    }
  }

  if (!res.ok) {
    const code = extractCode(parsed);
    const message = extractMessage(parsed) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, code, message, parsed);
  }
  return parsed as T;
}

function extractCode(body: unknown): string | null {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.message === 'string') return b.message;
    if (typeof b.error === 'string') return b.error;
    if (b.error && typeof b.error === 'object' && typeof (b.error as Record<string, unknown>).code === 'string') {
      return (b.error as Record<string, unknown>).code as string;
    }
  }
  return null;
}

function extractMessage(body: unknown): string | null {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.message === 'string') return b.message;
    if (typeof b.error === 'string') return b.error;
    if (b.error && typeof b.error === 'object' && typeof (b.error as Record<string, unknown>).message === 'string') {
      return (b.error as Record<string, unknown>).message as string;
    }
  }
  return null;
}
